import { type PimProductV11 } from '@redteclab/api/clients/bully'

import { TENANT } from '../../../tenant'

export const PRODUCT_REFERENCE_PRICE_TYPE = {
  AVP: 'AVP',
  UVP: 'UVP',
} as const

export type ProductReferencePriceType =
  (typeof PRODUCT_REFERENCE_PRICE_TYPE)[keyof typeof PRODUCT_REFERENCE_PRICE_TYPE]

const productAccessorIsMedicineReferencePriceProduct = ({
  product,
}: {
  product: PimProductV11
}): boolean => {
  const isPharmaObl = product.flags?.pharma_obl.active ?? false
  const isMedicament = product.flags?.medicament.active ?? false
  const isPrescriptionMedicine =
    product.flags?.prescription_medicine.active ?? false

  return isMedicament || isPharmaObl || isPrescriptionMedicine
}

const productAccessorIsBpcReferencePriceProduct = ({
  product,
}: {
  product: PimProductV11
}): boolean =>
  !product.flags?.pharma_obl.active &&
  !product.flags?.prescription_medicine.active

export const productAccessorGetReferencePriceType = ({
  product,
}: {
  product: PimProductV11
}): ProductReferencePriceType => {
  // Legal pricing communication. Pharma mandatory products use AVP, free/BPC
  // products use UVP
  if (productAccessorIsMedicineReferencePriceProduct({ product })) {
    return PRODUCT_REFERENCE_PRICE_TYPE.AVP
  }

  return PRODUCT_REFERENCE_PRICE_TYPE.UVP
}

export const productAccessorShouldUseReferencePrice = ({
  discount,
  isDiscountValid,
  isOfferMirakl,
  product,
  referencePriceAmount,
  tenant,
}: {
  discount: number
  isDiscountValid: boolean
  isOfferMirakl: boolean
  product: PimProductV11
  referencePriceAmount?: number | null
  tenant: TENANT
}): boolean => {
  // Apply the legal reference-price display only for COM tenant own-stock offers,
  // marketplace offers are excluded, and the discount must have a reference amount.
  return (
    tenant === TENANT.COM &&
    !isOfferMirakl &&
    discount > 0 &&
    isDiscountValid &&
    (productAccessorIsBpcReferencePriceProduct({ product }) ||
      productAccessorIsMedicineReferencePriceProduct({ product })) &&
    (referencePriceAmount ?? 0) > 0
  )
}
